import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { categoryEmoji } from "@/lib/finance/categories";
import { useFinanceResource, notifyFinanceChanged } from "@/hooks/useFinanceResource";
import { currencyOf, financePeriods, newFinanceId } from "@/lib/finance/summary";
import { localISODate, parseFinanceDecimal } from "@/lib/finance/input";
import { executeReviewedFinanceAction, acknowledgeFinanceAction, recoverPendingFinanceAction, readPendingFinanceAction, financeOwnerSession, financeAuthSessionId, invokeFinanceAction, buildReviewedFinanceRequest, newFinanceOperationId, releaseFailedFinanceAction, FinanceExecutorError, type FinanceActionRequest, type FinanceOwnerGuard, type FinanceEntity, type FinanceResponse } from "@/lib/financeExecutor";
import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";
export type TxType = "income" | "expense";
export type Recurrence = "fixed" | "variable" | "one_off";

export interface FinanceTx {
  id: string;
  user_id: string;
  emoji: string | null;
  name: string;
  category: string | null; // canonical slug (see lib/finance/categories)
  amount_exact?: string; money_safe?: boolean;
  amount: number; // POSITIVE magnitude — sign derived from `type` (matches v_finance_* views)
  type: TxType | null;
  recurrence: Recurrence | null;
  account_id: string | null;
  occurred_at: string | null;
  created_at: string;
  currency: string | null;
  note: string | null;
  source: string | null;
}

export type AccountType =
  | "checking" | "savings" | "cash" | "credit_card" | "investment" | "other";

export interface FinanceAccount {
  id: string;
  user_id: string;
  name: string;
  type: AccountType;
  currency: string | null;
  initial_balance: number; initial_balance_exact?: string; money_safe?: boolean;
  archived: boolean;
  created_at: string;
}

export interface FinanceBudget {
  id: string;
  user_id: string;
  category: string;
  recurrence_group: "fixed" | "variable";
  period_type: string; // "month"
  amount_planned: number; amount_planned_exact?: string; money_safe?: boolean;
  currency: string | null;
  active: boolean;
  starts_on: string | null;
  created_at: string;
}

export interface FinancePeriod {
  period: string;
  income: number;
  expense: number;
  net: number;
  tx_count: number;
}

export type Granularity = "daily" | "weekly" | "monthly" | "yearly";

const sb = supabase as unknown as {
  auth: typeof supabase.auth;
  from: (t: string) => any;
};

export interface NewTxInput {
  emoji: string | null;
  name: string;
  category: string | null; // canonical slug
  amount: number | string; // POSITIVE magnitude (sign comes from `type`)
  type: TxType;
  recurrence: Recurrence;
  account_id: string | null;
  occurred_at: string; // ISO
  note: string | null;
  currency?: string;
}

type Table = "finance_transactions" | "finance_accounts" | "finance_budgets";
interface Snapshot { transactions: FinanceTx[]; accounts: FinanceAccount[]; budgets: FinanceBudget[] }
type Assert = () => void;
const EMPTY: Snapshot = { transactions: [], accounts: [], budgets: [] };
type FinanceRows = { finance_transactions: FinanceTx; finance_accounts: FinanceAccount; finance_budgets: FinanceBudget };
export function normalizeFinanceRow<T extends Table>(table: T, row: Record<string, unknown>): FinanceRows[T] {
  const key = table === "finance_transactions" ? "amount" : table === "finance_accounts" ? "initial_balance" : "amount_planned";
  const raw = row[key + "_exact"] ?? row[key];
  if (typeof raw !== "string" && typeof raw !== "number") throw new Error("Invalid finance amount");
  const fullText = String(raw);
  if (!/^-?\d+(?:\.\d+)?$/.test(fullText)) throw new Error("Invalid finance amount");
  // Legacy PostgreSQL numerics may exceed the write envelope. Preserve their
  // exact text and keep affected aggregates unavailable instead of hiding rows.
  const exact = parseFinanceDecimal(fullText, { signed: true, zero: true }) ?? fullText;
  const number = Number(exact), cents = exact.replace("-", "").split(".");
  const safe = Number.isFinite(number) && Math.abs(number) <= Number.MAX_SAFE_INTEGER / 100 && (cents[1]?.length ?? 0) <= 2
    && (table === "finance_accounts" || !exact.startsWith("-"));
  return { ...row, [key]: number, [key + "_exact"]: exact, money_safe: safe } as unknown as FinanceRows[T];
}
async function readAll<T extends Table>(table: T, uid: string, assert: Assert) {
  const rows: FinanceRows[T][] = []; let cursor: string | undefined;
  const key = table === "finance_transactions" ? "amount" : table === "finance_accounts" ? "initial_balance" : "amount_planned";
  for (;;) {
    let query = sb.from(table).select("*," + key + "_exact:" + key + "::text").eq("user_id", uid).order("id", { ascending: true }).limit(500);
    if (cursor) query = query.gt("id", cursor);
    const { data, error } = await query; assert(); if (error) throw error;
    if (!Array.isArray(data) || data.some(row => row.user_id !== uid)) throw new Error("Finance read not confirmed");
    rows.push(...data.map(row => normalizeFinanceRow(table, row))); if (data.length < 500) return rows;
    const next = data[data.length - 1].id; if (!next || next === cursor) throw new Error("Finance cursor did not advance"); cursor = next;
  }
}
const txOrder = (a: FinanceTx, b: FinanceTx) => new Date(b.occurred_at || b.created_at).getTime() - new Date(a.occurred_at || a.created_at).getTime() || a.id.localeCompare(b.id);
async function loadFinance(uid: string, assert: Assert): Promise<Snapshot> {
  const [transactions, accounts, budgets] = await Promise.all([readAll("finance_transactions", uid, assert), readAll("finance_accounts", uid, assert), readAll("finance_budgets", uid, assert)]);
  transactions.sort(txOrder); accounts.sort((a, b) => a.created_at.localeCompare(b.created_at)); budgets.sort((a, b) => a.created_at.localeCompare(b.created_at));
  return { transactions, accounts, budgets };
}
const exactMoney = (input: number | string, signed = false, zero = false) => {
  const text = parseFinanceDecimal(String(input), { signed, zero });
  if (text === null || typeof input === "number" && (!Number.isFinite(input) || Math.abs(input) > Number.MAX_SAFE_INTEGER / 100)) throw new FinanceExecutorError("invalid_amount");
  return text;
};
export function useFinance() {
  const resource = useFinanceResource(loadFinance);
  const { userId, data, capture, identityGeneration, refresh } = resource;
  const [pendingFinance, setPendingFinance] = useState<PendingConnectorWrite | null>(null);
  const [saving, setSaving] = useState(false), busy = useRef<symbol | null>(null);
  const [pendingReview, setPendingReview] = useState<FinanceActionRequest | null>(null), [reviewRecovery, setReviewRecovery] = useState(false);
  const confirmLock = useRef(false);
  const [reviewBusy, setReviewBusy] = useState(false), [reviewError, setReviewError] = useState<string | null>(null);
  const reviewAttempt = useRef<{ request: FinanceActionRequest; owner: string; assert: FinanceOwnerGuard; operation: symbol; resolve: (result: FinanceResponse) => void; reject: (cause: unknown) => void; entry?: PendingConnectorWrite } | null>(null);
  useEffect(() => () => { reviewAttempt.current?.reject(new FinanceExecutorError("invalid_session")); reviewAttempt.current = null; }, []);
  useEffect(() => {
    if (reviewAttempt.current) { try { reviewAttempt.current.assert(); } catch (cause) { reviewAttempt.current.reject(cause); reviewAttempt.current = null; setPendingReview(null); setReviewRecovery(false); } }
    setPendingFinance(null);
    if (!reviewAttempt.current) { busy.current = null; confirmLock.current = false; setSaving(false); setReviewBusy(false); }
  }, [userId, identityGeneration]);
  useEffect(() => {
    if (!userId || !data) return;
    const current = capture(), owner = userId;
    const assert: FinanceOwnerGuard = () => { if (!current()) throw new FinanceExecutorError("invalid_session"); };
    void readPendingFinanceAction(owner, assert).then(entry => { assert(); setPendingFinance(entry); }).catch(() => {});
  }, [userId, identityGeneration, data, capture]);
  const transactions = data?.transactions ?? EMPTY.transactions, accounts = data?.accounts ?? EMPTY.accounts, budgets = data?.budgets ?? EMPTY.budgets;
  const aggregatesAvailable = [...transactions, ...accounts, ...budgets].every(row => row.money_safe !== false);
  const monthly = useMemo(() => aggregatesAvailable ? financePeriods(transactions, "monthly") : [], [transactions, aggregatesAvailable]);
  const stage = useCallback(async (entity: FinanceEntity, action: "create" | "update" | "delete", id: string, values?: Record<string, unknown>, context?: { session_id: string; turn_id: string }) => {
    if (busy.current) throw new FinanceExecutorError("finance_pending_action");
    const current = capture(), operation = Symbol(); busy.current = operation; setSaving(true); setReviewError(null);
    const assert: FinanceOwnerGuard = () => { if (!current()) throw new FinanceExecutorError("invalid_session"); };
    try {
      const owner = userId; if (!owner) throw new FinanceExecutorError("invalid_session");
      const session = await financeOwnerSession(owner, assert); assert.authSessionId = financeAuthSessionId(session.access_token) ?? undefined;
      const existing = await readPendingFinanceAction(owner, assert);
      let request: FinanceActionRequest;
      if (existing) {
        setPendingFinance(existing);
        throw new FinanceExecutorError("finance_pending_action");
      } else {
        const before = await invokeFinanceAction({ action: "get", entity, record_id: id }, owner, assert);
        if (!before.snapshot) throw new FinanceExecutorError("finance_read_unconfirmed");
        let expectedAccount;
        const accountId = entity === "transaction" && action !== "delete" ? values?.account_id === undefined ? before.snapshot.record?.account_id : values.account_id : null;
        if (accountId !== null && accountId !== undefined) {
          const account = await invokeFinanceAction({ action: "get", entity: "account", record_id: String(accountId) }, owner, assert);
          if (!account.snapshot?.record) throw new FinanceExecutorError("finance_read_unconfirmed"); expectedAccount = account.snapshot;
        }
        const operationId = newFinanceOperationId();
        request = buildReviewedFinanceRequest({ action, entity, record_id: id, operation_id: operationId, action_id: context?.turn_id ?? operationId,
          expected_user_id: owner, ...(context ? { expected_session_id: context.session_id } : {}) }, before.snapshot, values, owner, expectedAccount);
      }
      assert(); setPendingReview(structuredClone(request)); setReviewRecovery(!!existing); setReviewBusy(false);
      return await new Promise<FinanceResponse>((resolve, reject) => { reviewAttempt.current = { request: structuredClone(request), owner, assert, operation, resolve, reject, ...(existing ? { entry: existing } : {}) }; });
    } finally { if (busy.current === operation && !reviewAttempt.current) { busy.current = null; setSaving(false); } }
  }, [capture, userId]);
  const cancelReview = useCallback(() => {
    if (confirmLock.current) return;
    const attempt = reviewAttempt.current; if (!attempt) return;
    if (attempt.entry) setPendingFinance(attempt.entry);
    attempt.reject(new FinanceExecutorError("review_cancelled")); reviewAttempt.current = null; setPendingReview(null); setReviewRecovery(false);
    if (busy.current === attempt.operation) { busy.current = null; setSaving(false); }
  }, []);
  const confirmReview = useCallback(async () => {
    const attempt = reviewAttempt.current; if (!attempt || confirmLock.current) return;
    const { assert, owner } = attempt; confirmLock.current = true; setReviewBusy(true); setReviewError(null);
    try {
      assert();
      const completed = attempt.entry ? await recoverPendingFinanceAction(attempt.entry, assert) : await executeReviewedFinanceAction(owner, attempt.request, assert);
      assert(); attempt.entry = completed.entry;
      await acknowledgeFinanceAction(completed.entry, assert); assert();
      // A historical result cannot replace the current module snapshot.
      await refresh(); assert(); notifyFinanceChanged(owner);
      attempt.resolve(completed.response); reviewAttempt.current = null; setPendingReview(null); setReviewRecovery(false); setPendingFinance(null);
      if (busy.current === attempt.operation) { busy.current = null; setSaving(false); }
    } catch (cause) {
      try {
        assert(); const pending = await readPendingFinanceAction(owner, assert); assert();
        if (pending) { attempt.entry = pending; setPendingFinance(pending); setReviewRecovery(true); setReviewError((cause as Error).message); }
        else { attempt.reject(cause); reviewAttempt.current = null; setPendingReview(null); if (busy.current === attempt.operation) { busy.current = null; setSaving(false); } }
      } catch { /* Retired lifetime may not mutate a replacement actor. */ }
    } finally { try { assert(); confirmLock.current = false; setReviewBusy(false); } catch { /* retired actor */ } }
  }, [refresh]);
  const resumePendingReview = useCallback(() => {
    if (!pendingFinance || busy.current || pendingFinance.body.mirror_context) return;
    const current = capture(), owner = userId;
    if (!owner || pendingFinance.ownerId !== owner) return;
    const assert: FinanceOwnerGuard = () => { if (!current()) throw new FinanceExecutorError("invalid_session"); };
    const operation = Symbol(); busy.current = operation; setSaving(true);
    const request = pendingFinance.body as unknown as FinanceActionRequest;
    reviewAttempt.current = { request, owner, assert, operation, entry: pendingFinance, resolve: () => {}, reject: () => {} };
    setPendingReview(structuredClone(request)); setReviewRecovery(true); setReviewError(null);
  }, [pendingFinance, capture, userId]);
  const releaseReview = useCallback(async () => {
    const attempt = reviewAttempt.current;
    if (!attempt?.entry || confirmLock.current) return;
    confirmLock.current = true; setReviewBusy(true);
    try {
      await releaseFailedFinanceAction(attempt.entry, attempt.assert); attempt.assert();
      attempt.reject(new FinanceExecutorError("review_cancelled")); reviewAttempt.current = null;
      setPendingFinance(null); setPendingReview(null); setReviewRecovery(false); setReviewError(null);
      if (busy.current === attempt.operation) { busy.current = null; setSaving(false); }
    } catch (cause) { try { attempt.assert(); setReviewError((cause as Error).message); } catch { /* retired */ } }
    finally { try { attempt.assert(); confirmLock.current = false; setReviewBusy(false); } catch { /* retired */ } }
  }, []);
  const createTx = useCallback(async (input: NewTxInput, id = newFinanceId()): Promise<FinanceTx> => {
    const reply = await stage("transaction", "create", id, { ...input, amount: exactMoney(input.amount), currency: input.currency ?? "BRL", source: "manual", client_action_id: null });
    const record = reply.result?.after.record ?? reply.record?.record;
    if (!record) throw new FinanceExecutorError("outcome_unknown"); return normalizeFinanceRow("finance_transactions", record);
  }, [stage]);
  const updateTx = useCallback(async (id: string, patch: Partial<NewTxInput>): Promise<boolean> => {
    await stage("transaction", "update", id, { ...patch, ...(patch.amount !== undefined ? { amount: exactMoney(patch.amount) } : {}) }); return true;
  }, [stage]);
  const remove = useCallback(async (entity: FinanceEntity, id: string): Promise<boolean> => { await stage(entity, "delete", id); return true; }, [stage]);
  const createAccount = useCallback(async (input: { name: string; type: AccountType; initial_balance: number | string; currency?: string }, id = newFinanceId()): Promise<boolean> => {
    await stage("account", "create", id, { ...input, initial_balance: exactMoney(input.initial_balance, true, true), currency: input.currency ?? "BRL", archived: false }); return true;
  }, [stage]);
  const updateAccount = useCallback(async (id: string, patch: Partial<{ name: string; type: AccountType; initial_balance: number | string; archived: boolean; currency: string }>): Promise<boolean> => {
    await stage("account", "update", id, { ...(Object.keys(patch).length ? patch : { archived: accounts.find(row => row.id === id)?.archived ?? false }), ...(patch.initial_balance !== undefined ? { initial_balance: exactMoney(patch.initial_balance, true, true) } : {}) }); return true;
  }, [stage, accounts]);
  const createBudget = useCallback(async (input: { category: string; recurrence_group: "fixed" | "variable"; amount_planned: number | string; currency?: string }, id = newFinanceId()): Promise<boolean> => {
    await stage("budget", "create", id, { ...input, amount_planned: exactMoney(input.amount_planned, false, true), currency: input.currency ?? "BRL", period_type: "month", active: true, starts_on: localISODate().slice(0, 7) + "-01" }); return true;
  }, [stage]);
  const updateBudget = useCallback(async (id: string, patch: Partial<{ category: string; recurrence_group: "fixed" | "variable"; amount_planned: number | string; active: boolean; currency: string }>): Promise<boolean> => {
    await stage("budget", "update", id, { ...(Object.keys(patch).length ? patch : { active: budgets.find(row => row.id === id)?.active ?? true }), ...(patch.amount_planned !== undefined ? { amount_planned: exactMoney(patch.amount_planned, false, true) } : {}) }); return true;
  }, [stage, budgets]);
  const fetchPeriods = useCallback(async (g: Granularity, currency = "BRL"): Promise<FinancePeriod[]> => {
    if (!data || !userId || !aggregatesAvailable) throw new FinanceExecutorError("finance_totals_unavailable");
    return financePeriods(data.transactions, g, currency, 13);
  }, [data, userId, aggregatesAvailable]);
  const logVoiceTransaction = useCallback(async (input: { type: TxType; amount: number | string; category: string; name: string; occurred_at: string; note?: string | null; source?: "voice" | "text"; client_action_id?: string; currency?: string; mirror_context?: { session_id: string; turn_id: string } }): Promise<{ ok: boolean; duplicate: boolean; transaction: FinanceTx }> => {
    const id = newFinanceId();
    const reply = await stage("transaction", "create", id, { emoji: categoryEmoji(input.category), name: input.name, category: input.category, amount: exactMoney(input.amount), type: input.type, recurrence: "variable", account_id: null, occurred_at: input.occurred_at, note: input.note ?? null, currency: input.currency ?? "BRL", source: input.source ?? "voice", client_action_id: input.client_action_id ?? null }, input.mirror_context);
    const record = reply.result?.after.record ?? reply.record?.record;
    if (!record) throw new FinanceExecutorError("outcome_unknown");
    return { ok: true, duplicate: reply.replayed === true, transaction: normalizeFinanceRow("finance_transactions", record) };
  }, [stage]);
  return { userId: userId, loading: resource.status === "loading", loadError: resource.status === "error", status: resource.status, hasData: data !== null,
    saving, isRefreshing: resource.isRefreshing, capture: capture, transactions, accounts, budgets, monthly, reload: refresh, aggregatesAvailable,
    pendingReview, reviewRecovery, reviewBusy, reviewError, confirmReview, cancelReview, pendingFinance, resumePendingReview, releaseReview,
    createTx, updateTx, deleteTx: (id: string) => remove("transaction", id), createAccount, updateAccount, archiveAccount: (id: string) => updateAccount(id, { archived: true }),
    deleteAccount: (id: string) => remove("account", id), createBudget, updateBudget, deleteBudget: (id: string) => remove("budget", id), fetchPeriods, logVoiceTransaction };
}
